import { nanoid } from 'nanoid';
import type { AnyNode, CompNode, Sheet, WireEdge } from './store';
import type { ProjectMeta } from './types';

/** v1: flat `nodes`/`edges`. v2: `sheets[]` + `activeSheetId`. */
export interface ProjectFileV2 {
  version: 2;
  meta: ProjectMeta;
  sheets: Sheet[];
  activeSheetId: string;
}

interface ProjectFileV1 {
  version: 1;
  meta: ProjectMeta;
  nodes: AnyNode[];
  edges: WireEdge[];
}

export function serialize(
  meta: ProjectMeta,
  sheets: Sheet[],
  activeSheetId: string,
): string {
  const file: ProjectFileV2 = { version: 2, meta, sheets, activeSheetId };
  return JSON.stringify(file, null, 2);
}

export function deserialize(text: string): ProjectFileV2 {
  const parsed = JSON.parse(text);
  if (typeof parsed !== 'object' || !parsed.meta) {
    throw new Error('Incompatible project file.');
  }
  if (parsed.version === 2 && Array.isArray(parsed.sheets)) {
    return parsed as ProjectFileV2;
  }
  if (parsed.version === 1 && Array.isArray(parsed.nodes) && Array.isArray(parsed.edges)) {
    const v1 = parsed as ProjectFileV1;
    const schematicId = nanoid(8);
    return {
      version: 2,
      meta: v1.meta,
      sheets: [
        { id: nanoid(8), name: 'Block Diagram', kind: 'block', nodes: [], edges: [] },
        {
          id: schematicId,
          name: 'Schematic',
          kind: 'schematic',
          nodes: v1.nodes,
          edges: v1.edges,
        },
      ],
      activeSheetId: schematicId,
    };
  }
  throw new Error('Incompatible project file.');
}

export function downloadText(filename: string, text: string, mime = 'application/json') {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function pinLabel(pin: { name: string; group?: string } | undefined): string {
  if (!pin) return '?';
  return pin.group ? `${pin.group} / ${pin.name}` : pin.name;
}

export function generateWiringTable(nodes: CompNode[], edges: WireEdge[]): string {
  const rows: string[] = [
    '| # | From (Component : Connector / Pin) | To (Component : Connector / Pin) | Label | Gauge | Color | Notes |',
    '|---|-----------------------------------|----------------------------------|-------|-------|-------|-------|',
  ];
  edges.forEach((e, i) => {
    const src = nodes.find((n) => n.id === e.source);
    const tgt = nodes.find((n) => n.id === e.target);
    const srcPin = src?.data.pins.find((p) => p.id === e.sourceHandle);
    const tgtPin = tgt?.data.pins.find((p) => p.id === e.targetHandle);
    rows.push(
      `| ${i + 1} | ${src?.data.label ?? '?'} : ${pinLabel(srcPin)} | ${tgt?.data.label ?? '?'} : ${pinLabel(tgtPin)} | ${e.data?.label ?? ''} | ${e.data?.gauge ?? ''} | ${e.data?.color ?? ''} | ${e.data?.notes ?? ''} |`,
    );
  });
  return rows.join('\n');
}

export function generateRequirementsDoc(
  meta: ProjectMeta,
  nodes: CompNode[],
  edges: WireEdge[],
): string {
  const lines: string[] = [];
  lines.push(`# ${meta.name} — Requirements & System Specification`);
  lines.push('');
  if (meta.author) lines.push(`**Author:** ${meta.author}  `);
  lines.push(`**Created:** ${meta.createdAt.split('T')[0]}  `);
  lines.push(`**Updated:** ${meta.updatedAt.split('T')[0]}  `);
  lines.push('');
  if (meta.description) {
    lines.push('## Description');
    lines.push('');
    lines.push(meta.description);
    lines.push('');
  }

  lines.push('## Components');
  lines.push('');
  const byCategory = new Map<string, CompNode[]>();
  for (const n of nodes) {
    const cat = n.data.category || 'Other';
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat)!.push(n);
  }
  for (const [cat, items] of byCategory) {
    lines.push(`### ${cat}`);
    lines.push('');
    for (const n of items) {
      lines.push(`#### ${n.data.label}`);
      if (n.data.partNumber) lines.push(`- **Part:** ${n.data.partNumber}`);
      if (n.data.manufacturer) lines.push(`- **Manufacturer:** ${n.data.manufacturer}`);
      for (const [k, v] of Object.entries(n.data.properties)) {
        lines.push(`- **${k}:** ${v}`);
      }
      if (n.data.pins.length) {
        lines.push('');
        lines.push('| Connector | Pin | Side | Kind | Voltage | Notes |');
        lines.push('|-----------|-----|------|------|---------|-------|');
        for (const p of n.data.pins) {
          lines.push(
            `| ${p.group ?? ''} | ${p.name} | ${p.side} | ${p.kind} | ${p.voltage ?? ''} | ${p.notes ?? ''} |`,
          );
        }
      }
      lines.push('');
    }
  }

  lines.push('## Wiring');
  lines.push('');
  lines.push(generateWiringTable(nodes, edges));
  lines.push('');

  return lines.join('\n');
}
